import { useMergeLink } from '@mergeapi/react-merge-link'
import { createElement, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { toast } from 'sonner'

import { type PayrollProviderConfig } from 'utils/web_api.schemas'

import { useCreateMergeLinkToken } from './useCreateMergeLinkToken'
import { useExchangeMergePublicTokenForToken } from './useExchangeMergePublicTokenForToken'
import { usePostPayrollMergeSharedConnection } from '../../utils/web_api'
import { SharedConnectionConfirmationModal } from '../payroll/SharedConnectionConfirmationModal'

type ProviderCapabilities = {
  supportsAutomatedDeductions: boolean;
  supportsPaySchedules: boolean;
}

type OnSuccessCallback = (providerCapabilities: ProviderCapabilities) => void

interface UseMergeIntegrationProps {
  onSuccessCallback: OnSuccessCallback;
}

export function useMergeIntegration ({ onSuccessCallback }: UseMergeIntegrationProps) {
  const [linkToken, setLinkToken] = useState<string | undefined>(undefined)
  const [providerIdToOpen, setProviderIdToOpen] = useState<string | undefined>(undefined)
  const [pendingSharedConnection, setPendingSharedConnection] = useState<PayrollProviderConfig | null>(null)
  const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null)
  const [modalRoot, setModalRoot] = useState<any>(null)

  const createLinkTokenMutation = useCreateMergeLinkToken()
  const exchangePublicTokenMutation = useExchangeMergePublicTokenForToken()
  const attachToSharedConnectionMutation = usePostPayrollMergeSharedConnection()

  const handleSuccess = (publicToken: string) => {
    exchangePublicTokenMutation.mutate({ publicToken }, {
      onSuccess: () => {
        onSuccessCallback({
          supportsAutomatedDeductions: false,
          supportsPaySchedules: false
        })
      },
      onError: () => {
        toast.error('Error connecting to payroll provider, please try again or contact support.')
      }
    })
  }

  const { open, isReady } = useMergeLink({
    linkToken: linkToken || '',
    onSuccess: handleSuccess,
    onExit: () => {
      setLinkToken(undefined)
    }
  })

  const startConnection = async (providerConfig: PayrollProviderConfig) => {
    // If this is a shared connection, set up for modal confirmation
    if (providerConfig.shared_connection_name) {
      setPendingSharedConnection(providerConfig)
      return Promise.resolve()
    } else {
      // Regular merge connection flow
      setProviderIdToOpen(providerConfig.id)

      return new Promise<void>((resolve) => {
        createLinkTokenMutation.mutate({ payrollProviderConfigId: providerConfig.id }, {
          onSuccess: ({ link_token: linkToken }) => {
            setLinkToken(linkToken)
            resolve()
          },
          onError: () => {
            toast.error('Error connecting to payroll provider, please try again.')
            resolve()
          }
        })
      })
    }
  }

  const handleSharedConnectionConfirm = () => {
    if (!pendingSharedConnection) return

    attachToSharedConnectionMutation.mutate({
      data: {
        api_connection_remote_id: pendingSharedConnection.api_connection_remote_id,
        shared_connection_name: pendingSharedConnection.shared_connection_name!
      }
    }, {
      onSuccess: () => {
        cleanup()
        onSuccessCallback({
          supportsAutomatedDeductions: false,
          supportsPaySchedules: false
        })
      },
      onError: () => {
        toast.error('Error connecting to payroll provider, please try again or contact support.')
        cleanup()
      }
    })
  }

  const renderModal = () => {
    if (!modalContainer || !modalRoot || !pendingSharedConnection) return

    modalRoot.render(
      createElement(SharedConnectionConfirmationModal, {
        open: true,
        onOpenChange: cleanup,
        onConfirm: handleSharedConnectionConfirm,
        providerConfig: pendingSharedConnection,
        isLoading: attachToSharedConnectionMutation.isPending
      })
    )
  }

  const closeModal = () => {
    if (modalRoot) {
      modalRoot.unmount()
      setModalRoot(null)
    }
    if (modalContainer && document.body.contains(modalContainer)) {
      document.body.removeChild(modalContainer)
      setModalContainer(null)
    }
  }

  const cleanup = () => {
    setProviderIdToOpen(undefined)
    setPendingSharedConnection(null)
    closeModal()
  }

  const openModal = () => {
    // If there's a pending shared connection, show the confirmation modal
    if (pendingSharedConnection) {
      // Create modal container
      const container = document.createElement('div')
      container.id = 'shared-connection-modal-portal'
      document.body.appendChild(container)
      setModalContainer(container)

      // Create React root and render modal
      const root = createRoot(container)
      setModalRoot(root)

      // Render the modal
      root.render(
        createElement(SharedConnectionConfirmationModal, {
          open: true,
          onOpenChange: cleanup,
          onConfirm: handleSharedConnectionConfirm,
          providerConfig: pendingSharedConnection,
          isLoading: attachToSharedConnectionMutation.isPending
        })
      )
      return
    }

    // Regular merge connection flow
    if (linkToken && providerIdToOpen && isReady) {
      open()
    }
  }

  // Re-render the modal whenever the loading state changes so the UI stays in sync
  useEffect(() => {
    if (modalRoot && pendingSharedConnection) {
      renderModal()
    }
  }, [attachToSharedConnectionMutation.isPending, modalRoot, pendingSharedConnection])

  return {
    startConnection,
    openModal,
    linkToken,
    isReady,
    providerIdToOpen,
    setProviderIdToOpen,
    modalContainer,
    pendingSharedConnection
  }
}
